import useStore from "@/shared/store/useStore";
import { useEffect, useMemo, useState } from "react";
import SectionCard from "./SectionCard";
import StatCard from "./StatCard";
import { Box } from "@/components/ui/box";
import { PieChartSimple } from "./PieChartSimple";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { DriverChart } from "./DriverChart";
import { useDashboard } from "../hooks/useDashboard";
import { MonthRangeFilter } from "@/components/ui/custom/MonthRangeFilter";
import { cn, formatWIB, formatWIBOffset } from "@/shared/lib/utils";
import { Spinner } from "@/components/ui/spinner";
import { MASTER_DATA_MOTORCYCLE } from "@/shared/constants/path";
import Link from "next/link";
import useFetchMotorcycleRentStatus from "@/shared/hooks/useFetchMotorcycleRentStatus";

/* ================= TYPES ================= */
export type RangePeriod = { start: Date; end: Date };

type MotorRentStatusItem = {
  name: string;
  type: string;
  total: number;
  description: string;
  rent_status_id?: string;
};

/* ================= HELPERS ================= */

// "Motor Baru" → "MOTOR_BARU"
const toEnumKey = (label: string) =>
  label.toUpperCase().replace(/\s+/g, "_").replace(/-+/g, "_");

/* ================= COMPONENT ================= */

export function Dashboard() {
  const { user } = useStore();
  // useEffect(() => {}, [user]);

  const [activeCard, setActiveCard] = useState("product");
  const [rangePeriod, setRangePeriod] = useState<RangePeriod | undefined>(
    undefined
  );

  const payload = useMemo(() => {
    if (!rangePeriod) return undefined;

    return {
      start_date: formatWIBOffset(rangePeriod.start),
      end_date: formatWIBOffset(rangePeriod.end, true),
    };
  }, [rangePeriod]);

  const { data, isLoading, isFetching } = useDashboard(payload);
  const { motorcycleRentStatusOptions, isLoading: isLoadingRentStatus } =
    useFetchMotorcycleRentStatus(true);

  /* ================= RENT STATUS LOOKUP ================= */

  const rentStatusMap = useMemo(() => {
    if (!motorcycleRentStatusOptions) return {};

    return motorcycleRentStatusOptions.reduce<Record<string, string>>(
      (acc, item) => {
        const enumKey = item.label.split(" - ")[0]; // MOTOR_BARU
        acc[enumKey] = item.value; // "1"
        return acc;
      },
      {}
    );
  }, [motorcycleRentStatusOptions]);

  const safeValue = (value?: number) =>
    isLoading || isFetching ? undefined : value ?? 0;

  const handleRangeChange = (value?: RangePeriod) => {
    if (!value) return; // ignore clear
    setRangePeriod(value);
  };

  const dataMotor = [
    {
      status: "online",
      unit: data?.total_motor_iot_active || 0,
      fill: "#1F9545",
      label: "Status online",
    },
    {
      status: "offline",
      unit: data?.total_motor_iot_inactive || 0,
      fill: "#F9C700",
      label: "Status offline",
    },
  ];

  const driverData = [
    { status: "new", total: data?.total_driver_new || 0 },
    { status: "active", total: data?.total_driver_active || 0 },
    {
      status: "waiting-approval",
      total: data?.total_driver_waiting_approval || 0,
    },
    { status: "rejected", total: data?.total_driver_rejected || 0 },
  ];

  const totalActivation = [
    {
      label: "Sewa Milik",
      aktif: data?.total_activation_active,
      tidakAktif: data?.total_activation_inactive,
    },
  ];

  const dataSimCard = [
    {
      label: "Aktif dan Terpasang di IoT Device",
      total: data?.total_sim_active_with_iot || 0,
    },
    {
      label: "Aktif dan Tidak Terpasang di IoT Device",
      total: data?.total_sim_active_without_iot || 0,
    },
    {
      label: "Expired dan Terpasang di IoT Device",
      total: data?.total_sim_expired_with_iot || 0,
    },
    {
      label: "Expired dan Tidak Terpasang di IoT Device",
      total: data?.total_sim_expired_without_iot || 0,
    },
  ];

  const dataMotorByRentStatus: MotorRentStatusItem[] = useMemo(() => {
    const baseData: Omit<MotorRentStatusItem, "rent_status_id">[] = [
      {
        name: "Motor Baru",
        type: "MOTOR_BARU",
        total: data?.motor_new || 0,
        description:
          "Unit motor baru (input unit motor baru, belum pernah sewa/stop sewa)",
      },
      {
        name: "Motor Second",
        type: "MOTOR_BEKAS",
        total: data?.motor_secondhand || 0,
        description:
          "Motor bekas pakai atau pernah disewa sebelumnya (stop sewa, kemudian dilanjutkan user lain)",
      },
      {
        name: "Motor Disewa",
        type: "MOTOR_SEWA",
        total: data?.motor_rent || 0,
        description: "Motor sedang dalam status disewa dan aktif dipakai user",
      },
      {
        name: "Motor Ditarik",
        type: "MOTOR_DITARIK",
        total: data?.motor_retrieved || 0,
        description:
          "Motor ditarik kembali oleh perusahaan karena pelanggaran atau selesai sewa",
      },
      {
        name: "Motor Lunas",
        type: "MOTOR_PAID_OFF",
        total: data?.motor_paid_off || 0,
        description: "Motor lunas",
      },
      {
        name: "Rental Under Maintenance",
        type: "RENT_UNDER_MAINTENANCE",
        total: data?.rent_under_maintenance || 0,
        description: "Motor yang sedang disewa namun dalam perbaikan",
      },
      {
        name: "Motor Dicuri",
        type: "MOTOR_RENT_STOLEN",
        total: data?.motor_rent_stolen || 0,
        description:
          "Motor yang sedang disewa namun diset idle karena dicuri atau hilang",
      },
      {
        name: "Motor Hilang",
        type: "MOTOR_HILANG",
        total: data?.motor_missing || 0,
        description:
          "Motor tidak ditemukan/kehilangan (potensi fraud atau kehilangan fisik)",
      },
      {
        name: "Motor Rusak",
        type: "MOTOR_RUSAK",
        total: data?.motor_broken || 0,
        description:
          "Motor mengalami kerusakan dan tidak dapat digunakan sementara",
      },
      {
        name: "Second Under Maintenance",
        type: "SECOND_UNDER_MAINTENANCE",
        total: data?.second_under_maintenance || 0,
        description: "Motor bekas sewa, namun dalam perbaikan",
      },
      {
        name: "Motor Stock",
        type: "MOTOR_STOK",
        total: data?.motor_stock || 0,
        description: "Motor tersedia dan siap disewakan/dioperasikan",
      },
      {
        name: "Motor Stock In",
        type: "MOTOR_STOK_MASUK",
        total: data?.motor_stock_in || 0,
        description: "Motor masuk ke dalam stok pool (barang diterima)",
      },
      {
        name: "Motor Stock Out",
        type: "MOTOR_STOK_KELUAR",
        total: data?.motor_stock_out || 0,
        description:
          "Motor keluar dari stok pool untuk proses distribusi/penyewaan",
      },
      {
        name: "Motor Maintenance",
        type: "MOTOR_PERBAIKAN",
        total: data?.motor_maintenance || 0,
        description: "Motor dalam proses perbaikan akibat kerusakan",
      },
      {
        name: "Motor Scrap",
        type: "MOTOR_SCRAP",
        total: data?.motor_scrapped || 0,
        description:
          "Motor dalam kondisi tidak layak pakai dan akan dipreteli/scrap",
      },
      {
        name: "Motor Dikirim",
        type: "MOTOR_DIKIRIM",
        total: data?.motor_sent || 0,
        description:
          "Motor sedang dalam proses pengiriman ke lokasi/pool tertentu",
      },
      {
        name: "Motor Dikunci",
        type: "MOTOR_DIKUNCI",
        total: data?.motor_locked || 0,
        description:
          "Motor dinonaktifkan secara sistem karena pelanggaran/tunggakan",
      },
    ];

    return baseData.map((item) => {
      const enumKey = toEnumKey(item.type);

      return {
        ...item,
        rent_status_id: rentStatusMap[enumKey],
      };
    });
  }, [data, rentStatusMap]);

  /* ================= RENDER ================= */

  return (
    <Box className="space-y-6 pb-6">
      <Box>
        <h1 className="text-3xl font-semibold">Hi, {user?.role_name}!</h1>
        <p className="text-base font-normal text-gray-500">
          Kelola semua transaksi yang ada di gesit volta hanya dalam satu
          dashboard.
        </p>
      </Box>

      {/* <Box className="grid gap-4 grid-cols-1 lg:grid-cols-10 lg:grid-rows-[126px_126px_126px_126px_126px_126px] lg:h-fit lg:max-h-[500px]"> */}
      <Box className="grid gap-4 grid-cols-1 xl:grid-cols-12 xl:grid-rows-[126px_126px_126px_200px_200px]">
        {/* MOTOR */}
        <SectionCard
          title="Total Motor by IoT Status"
          className="xl:row-start-2 xl:col-span-3 xl:row-span-3 h-full"
        >
          <Box className="flex flex-col items-center justify-center gap-4 text-gray-400 h-fit">
            <PieChartSimple data={dataMotor} dataKey="unit" nameKey="status" />

            {/* Total */}
            <Box className="flex flex-col gap-6">
              {isLoading || isFetching ? (
                <Spinner className="h-6 w-6" />
              ) : (
                <Box className="text-center text-2xl font-semibold text-[#212121]">
                  {safeValue(data?.total_motorcycle)} unit
                </Box>
              )}

              {/* Legends */}
              <Box className="flex flex-col gap-2">
                {dataMotor?.map((item) => (
                  <Box
                    key={item.status as string}
                    className="flex items-center gap-2 text-sm text-[#212121]"
                  >
                    <Box
                      className="h-3 w-3 rounded-full shrink-0"
                      style={{ backgroundColor: item.fill }}
                    />

                    <Box className="flex flex-wrap items-center gap-2 font-semibold">
                      {item.label}:
                      {isLoading || isFetching ? (
                        <Spinner className="h-6 w-6" />
                      ) : (
                        <span className="font-normal">{item.unit} unit</span>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </SectionCard>

        <StatCard
          title="Total Cabinet SiGeulis"
          value={safeValue(data?.total_cabinet)}
          loading={isLoading || isFetching}
          subtitle="cabinet"
          type="cabinet"
          variant={activeCard === "product" ? "success" : "default"}
          onClick={() => setActiveCard("product")}
          className="xl:col-start-1 xl:col-span-4 xl:row-start-1 xl:h-[126px] h-full"
        />
        <StatCard
          title="Total Pembelian Paket Rental"
          value={safeValue(data?.total_package_purchased)}
          loading={isLoading || isFetching}
          subtitle="transaksi"
          type="report"
          variant={activeCard === "report" ? "success" : "default"}
          onClick={() => setActiveCard("report")}
          className="xl:col-start-5 xl:col-span-4 xl:row-start-1 xl:h-[126px] h-full"
        />
        <StatCard
          title="Total Transaksi Charging"
          value={safeValue(data?.total_transaction_charging_battery)}
          loading={isLoading || isFetching}
          subtitle="transaksi"
          variant={activeCard === "send" ? "success" : "default"}
          type="send"
          onClick={() => setActiveCard("send")}
          className="xl:col-start-9 xl:col-span-4 xl:row-start-1 xl:h-[126px] h-full"
        />

        {/* DRIVER – CENTER (40%) */}
        <SectionCard
          title="Total Driver"
          // className="xl:row-start-2 xl:col-start-4 xl:col-span-5 xl:row-span-3 flex flex-col h-full"
          className="xl:col-start-4 xl:col-span-6 xl:row-start-2 xl:row-span-3 flex flex-col h-full"
        >
          {/* Header */}
          <Box className="flex items-center gap-2 mt-6">
            {isLoading || isFetching ? (
              <Spinner className="h-6 w-6" />
            ) : (
              <Box className="text-3xl font-semibold text-[#212121]">
                {safeValue(data?.total_driver)}
              </Box>
            )}
            <Box className="text-sm font-normal text-[#6E6E6E]">orang</Box>
          </Box>

          {/* Chart */}
          <Box className="flex-1 mt-4 min-h-[100px]">
            <DriverChart data={driverData} barColor="#1F9545" />
          </Box>
        </SectionCard>

        {/* POOL */}
        <StatCard
          title="Total Pool"
          value={safeValue(data?.total_pool)}
          loading={isLoading || isFetching}
          subtitle="aktif"
          variant={activeCard === "pool" ? "success" : "default"}
          // className="xl:row-start-5 xl:col-start-4 xl:col-span-3 xl:h-[126px] h-full"
          className="xl:col-start-1 xl:col-span-2 xl:row-start-5 xl:h-[200px] h-full"
          onClick={() => setActiveCard("pool")}
        />

        {/* PIC */}
        <StatCard
          title="Total PIC"
          value={safeValue(data?.total_pic_driver_active)}
          loading={isLoading || isFetching}
          subtitle="aktif"
          value2={safeValue(data?.total_pic_driver_inactive)}
          subtitle2="tidak aktif"
          variant={activeCard === "pic" ? "success" : "default"}
          type="pic"
          // className="xl:row-start-5 xl:col-start-7 xl:col-span-5 xl:h-[126px] h-full"
          className="xl:col-start-3 xl:col-span-3 xl:row-start-5 xl:h-[200px] h-full"
          onClick={() => setActiveCard("pic")}
        />

        <SectionCard
          title="Total SIM Card"
          className="xl:col-start-6 xl:col-span-7 xl:row-start-5 xl:h-[200px] h-full"
        >
          <Box className="mt-5 flex flex-col gap-3">
            {dataSimCard?.map((item, idx) => (
              <Box key={idx} className="flex justify-between gap-3">
                <Box className="flex items-center gap-2">
                  <Box className="text-sm font-normal text-[#212121]">
                    {item.label}
                  </Box>
                </Box>
                {isLoading || isFetching ? (
                  <Spinner className="h-6 w-6" />
                ) : (
                  <span className="text-sm font-medium text-[#212121]">
                    {item?.total}
                  </span>
                )}
              </Box>
            ))}
          </Box>
        </SectionCard>

        {/* AKTIVASI – RIGHT (30%) */}
        <SectionCard
          title="Total Aktivasi & Rental"
          description="Lihat semua aktivasi dan rental disini"
          // className="xl:row-start-2 xl:col-start-9 xl:col-span-4 xl:row-span-3 flex flex-col justify-between gap-4 h-full"
          className="xl:col-start-10 xl:col-span-3 xl:row-start-2 xl:row-span-3 flex flex-col justify-between gap-4 h-full"
          filterSlot={
            <MonthRangeFilter
              value={rangePeriod}
              onChange={handleRangeChange}
              className="w-full h-[37px]"
            />
          }
        >
          <Box className="flex flex-col gap-3">
            <span className="text-base font-semibold text-[#212121]">
              Total Paket Rental Motor
            </span>

            <Box className="flex justify-between">
              <Box className="flex items-center gap-2">
                <Box className="text-sm font-normal text-[#212121]">
                  Total Paket Harian Aktif
                </Box>
              </Box>
              {isLoading || isFetching ? (
                <Spinner className="h-6 w-6" />
              ) : (
                <span className="text-sm font-medium text-[#212121]">
                  {data?.total_rental_active}
                </span>
              )}
            </Box>
            <Box className="flex justify-between">
              <Box className="flex items-center gap-2">
                <Box className="text-sm font-normal text-[#212121]">
                  Total Paket Harian Expired
                </Box>
              </Box>
              {isLoading || isFetching ? (
                <Spinner className="h-6 w-6" />
              ) : (
                <span className="text-sm font-medium text-[#212121]">
                  {data?.total_rental_expired}
                </span>
              )}
            </Box>
          </Box>

          <Separator />

          <Box className="flex flex-col gap-3">
            <span className="text-base font-semibold text-[#212121]">
              Total Paket Aktivasi Motor
            </span>
            {totalActivation?.map((item) => (
              <Box key={item.label} className="flex justify-between">
                <Box className="flex items-center gap-2">
                  <Box className="text-sm font-normal text-[#212121]">
                    Total Transaksi Aktif
                  </Box>
                </Box>
                {isLoading || isFetching ? (
                  <Spinner className="h-6 w-6" />
                ) : (
                  <span className="text-sm font-medium text-[#212121]">
                    {item.aktif}
                  </span>
                )}
              </Box>
            ))}
            {totalActivation?.map((item) => (
              <Box key={item.label + "-tidak"} className="flex justify-between">
                <Box className="flex items-center gap-2">
                  <Box className="text-sm font-normal text-[#212121]">
                    Total Transaksi Tidak Aktif
                  </Box>
                </Box>
                {isLoading || isFetching ? (
                  <Spinner className="h-6 w-6" />
                ) : (
                  <span className="text-sm font-medium text-[#212121]">
                    {item.tidakAktif}
                  </span>
                )}
              </Box>
            ))}
          </Box>
        </SectionCard>
      </Box>

      <SectionCard title="Total Motor by Status">
        <Box
          className="mt-5 columns-1 sm:columns-2 lg:columns-3 gap-10"
          style={{ columnRule: "1px solid #E5E7EB" }}
        >
          {dataMotorByRentStatus.map((item) => (
            <Link
              key={item.name}
              href={
                item.rent_status_id
                  ? `${MASTER_DATA_MOTORCYCLE.LIST}?rent_status_id=${item.rent_status_id}`
                  : "#"
              }
              title={item.description}
              className={cn(
                "group mb-3 break-inside-avoid flex items-center gap-2",
                "text-[rgb(33,33,33)] hover:text-red-500 transition-colors",
                !item.rent_status_id && "pointer-events-none opacity-50"
              )}
            >
              {/* Label */}
              <span className="text-sm shrink-0 text-current">{item.name}</span>

              {/* Dotted leader */}
              <span className="flex-1 border-b border-dotted border-current opacity-50" />

              {/* Value */}
              {isLoading || isFetching ? (
                <Spinner className="h-4 w-4 text-current" />
              ) : (
                <span className="text-sm font-medium shrink-0 text-current">
                  {item.total}
                </span>
              )}
            </Link>
          ))}
        </Box>
      </SectionCard>
    </Box>
  );
}
